import { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { InitialStateUserType } from '../../../typings/state';
import { CVProfile, CVIdentityData } from '../types';

export interface ProfileSyncConfig {
  onProfileUpdate?: (profileData: CVIdentityData) => void;
  enabled?: boolean;
}

/**
 * Hook for synchronizing user profile changes with CV identity data
 * Listens for profile updates and automatically updates CV identity section
 */
export const useProfileSync = (config: ProfileSyncConfig = {}) => {
  const { onProfileUpdate, enabled = true } = config;
  
  // Get user profile from Redux store
  const user = useSelector((state: { user: InitialStateUserType }) => state.user);
  const userProfile = user.profile;

  // Convert user profile to CV identity data format
  const convertProfileToCVData = useCallback((profile: any): CVIdentityData => {
    return {
      full_name: profile?.fullname || '',
      birth_place: '', // Not available in user profile
      birth_date: profile?.dob || '',
      address: '', // Not available in user profile
      phone_number: profile?.phone || '',
      email: profile?.email || '',
      linkedin_url: '', // Not available in user profile
      portfolio_url: '', // Not available in user profile
      dream_job: '', // CV-specific field
      main_interest: '', // CV-specific field
      interest_reason: '', // CV-specific field
      profile_summary: '' // CV-specific field
    };
  }, []);

  // Handle profile updates
  useEffect(() => {
    if (!enabled || !userProfile || !onProfileUpdate) {
      return;
    }

    // Convert profile data and call update handler
    const cvIdentityData = convertProfileToCVData(userProfile);
    onProfileUpdate(cvIdentityData);
  }, [userProfile, onProfileUpdate, convertProfileToCVData, enabled]);

  return {
    userProfile,
    convertProfileToCVData
  };
};

/**
 * Hook for detecting profile changes and providing merge functionality
 */
export const useProfileMerge = (currentCVData: CVProfile | null) => {
  const user = useSelector((state: { user: InitialStateUserType }) => state.user);
  const userProfile = user.profile;

  // Check if profile data differs from CV data
  const hasProfileChanges = useCallback((): boolean => {
    if (!userProfile || !currentCVData) {
      return false;
    }

    return (
      userProfile.fullname !== currentCVData.full_name ||
      userProfile.email !== currentCVData.email ||
      userProfile.phone !== currentCVData.phone_number ||
      userProfile.dob !== currentCVData.birth_date
    );
  }, [userProfile, currentCVData]);

  // Merge profile data with existing CV data
  const mergeProfileData = useCallback((cvData: CVProfile): CVProfile => {
    if (!userProfile) {
      return cvData;
    }

    return {
      ...cvData,
      full_name: userProfile.fullname || cvData.full_name,
      email: userProfile.email || cvData.email,
      phone_number: userProfile.phone || cvData.phone_number,
      birth_date: userProfile.dob || cvData.birth_date
    };
  }, [userProfile]);

  // Get the changes that would be applied
  const getProfileChanges = useCallback((): Partial<CVProfile> => {
    if (!userProfile || !currentCVData) {
      return {};
    }

    const changes: Partial<CVProfile> = {};

    if (userProfile.fullname && userProfile.fullname !== currentCVData.full_name) {
      changes.full_name = userProfile.fullname;
    }
    if (userProfile.email && userProfile.email !== currentCVData.email) {
      changes.email = userProfile.email;
    }
    if (userProfile.phone && userProfile.phone !== currentCVData.phone_number) {
      changes.phone_number = userProfile.phone;
    }
    if (userProfile.dob && userProfile.dob !== currentCVData.birth_date) {
      changes.birth_date = userProfile.dob;
    }

    return changes;
  }, [userProfile, currentCVData]);

  return {
    hasProfileChanges: hasProfileChanges(),
    mergeProfileData,
    getProfileChanges,
    userProfile
  };
};